import { useTranslation } from '@/shared/contexts/TranslationContext'
import { LOCALE_CURRENCY } from '@/shared/components/ui/CurrencyInput'
import type { AssetHolding } from '@/modules/inventory/holdings'

export interface BatchAdjustmentFormData {
  new_initial_quantity: string
  new_total_cost: string
}

interface BatchAdjustmentFormProps {
  batch: AssetHolding | null
  formData: BatchAdjustmentFormData
  setFormData: (data: BatchAdjustmentFormData) => void
  formErrors: Record<string, string>
  submitting: boolean
  tc?: (key: string) => string
}

export function BatchAdjustmentForm({
  batch,
  formData,
  setFormData,
  formErrors,
  submitting,
  tc: tcProp,
}: BatchAdjustmentFormProps) {
  const { t, locale } = useTranslation()
  const tc = tcProp || ((key: string) => t(`registrations.batches.adjustment.${key}`))

  if (!batch) return null

  const readOnly = submitting
  const currentQty = batch.initial_quantity ?? 0
  const currentCost = batch.total_cost ?? 0
  const newQty = parseFloat(formData.new_initial_quantity || '0')
  const newCost = parseFloat(formData.new_total_cost || '0')
  const qtyDiff = newQty - currentQty
  const costDiff = newCost - currentCost

  const currency = LOCALE_CURRENCY[locale] ?? 'BRL'
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)

  const inputCls = (field: string) =>
    `w-full px-4 py-2 rounded-lg border ${
      formErrors[field] ? 'border-red-300' : 'border-slate-300'
    } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500`

  return (
    <div className="space-y-6">
      <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{tc('batch')}:</span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white">
              {batch.name ?? batch.asset?.name ?? batch.id}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{tc('assetType')}:</span>
            <span className="text-sm text-slate-900 dark:text-white">{batch.asset?.name ?? '—'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wide">
            {tc('fields.currentQuantity')}
          </label>
          <div className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-sm">
            {Number(currentQty).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {batch.uom}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wide">
            {tc('fields.currentCost')}
          </label>
          <div className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-sm">
            {formatCurrency(currentCost)}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">{tc('newValues')}</h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('fields.newQuantity')} {batch.uom}
            </label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={formData.new_initial_quantity}
              onChange={(e) => setFormData({ ...formData, new_initial_quantity: e.target.value })}
              placeholder={String(currentQty)}
              className={inputCls('new_initial_quantity')}
              disabled={readOnly}
            />
            {formErrors.new_initial_quantity && (
              <p className="mt-1 text-sm text-red-600">{formErrors.new_initial_quantity}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('fields.newCost')}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.new_total_cost}
              onChange={(e) => setFormData({ ...formData, new_total_cost: e.target.value })}
              placeholder={String(currentCost)}
              className={inputCls('new_total_cost')}
              disabled={readOnly}
            />
            {formErrors.new_total_cost && (
              <p className="mt-1 text-sm text-red-600">{formErrors.new_total_cost}</p>
            )}
          </div>
        </div>
      </div>

      {(formData.new_initial_quantity || formData.new_total_cost) && (
        <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">{tc('differences')}</h3>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                {tc('fields.quantityDiff')}
              </div>
              <div className={`text-lg font-bold ${qtyDiff > 0 ? 'text-green-600 dark:text-green-400' : qtyDiff < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-400'}`}>
                {qtyDiff > 0 ? '+' : ''}{Number(qtyDiff).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {batch.uom}
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
              <div className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">
                {tc('fields.costDiff')}
              </div>
              <div className={`text-lg font-bold ${costDiff > 0 ? 'text-red-600 dark:text-red-400' : costDiff < 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-600 dark:text-slate-400'}`}>
                {formatCurrency(costDiff)}
              </div>
            </div>
          </div>

          <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              {tc('info')}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
